import { useCallback, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';
import rem from 'polished/lib/helpers/rem';

import { spacing } from '@decisiv/design-tokens';

import Trash from '@decisiv/iconix/lib/components/Trash';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';

import EnabledFormTag from 'components/EnabledFormTag';
import DataRefreshFormTag from 'components/DataRefreshFormTag';
import { useTeams } from 'pages/Teams/contexts/TeamsContext';
import DeleteTeamModal from './DeleteTeamModal';

type Props = {
  rowData: Pick<
    SentinelTeam,
    'id' | 'name' | 'enabled' | 'scheduledForDeletionAt'
  > &
    Partial<Pick<SentinelTeam, 'dataRefreshEnabled'>>;
};

const FlexWithGap = styled(Flex)`
  gap: ${rem(spacing.base * 0.5)};
` as typeof Flex;

export default function ActionsCell({
  rowData: { id, name, enabled, dataRefreshEnabled, scheduledForDeletionAt },
}: Props) {
  const buttonRef = useRef(null);

  const [deleteTeamModalVisible, setTeamModalVisible] = useState(false);

  const handleOnDeleteModalToggle = useCallback(() => {
    setTeamModalVisible((oldState) => !oldState);
  }, []);

  const teamsContext = useTeams();
  const environment =
    'environment' in teamsContext ? (teamsContext.environment as string) : '';

  const shouldShowDeleteButton = !!environment && !environment.match(/prod/i);

  return (
    <FlexWithGap alignItems="center">
      <FlexWithGap flexDirection="column" alignItems="flex-start">
        <EnabledFormTag
          teamId={id}
          enabled={enabled}
          name={name}
          preventClick={!!scheduledForDeletionAt}
        />
        {enabled && (
          <DataRefreshFormTag
            teamId={id}
            dataRefreshEnabled={dataRefreshEnabled}
            name={name}
            preventClick={!!scheduledForDeletionAt}
          />
        )}
      </FlexWithGap>
      {shouldShowDeleteButton && (
        <>
          <Flex ref={buttonRef}>
            <Button
              icon={Trash}
              variant="ghost"
              disabled={!!scheduledForDeletionAt}
              aria-label={t`Delete Sentinel Team ${id}`}
              onClick={handleOnDeleteModalToggle}
            />
          </Flex>
          <Tooltip target={buttonRef} zIndex={10} placement="left">
            {t`Delete Team`}
          </Tooltip>
          {deleteTeamModalVisible && (
            <DeleteTeamModal
              teamId={id}
              teamName={name}
              onClose={handleOnDeleteModalToggle}
            />
          )}
        </>
      )}
    </FlexWithGap>
  );
}
